// @ts-check
import moment from 'moment-timezone';
import editShiftErrorModal from '../edit-shift-status-modals/edit-error-modal.html';
import editShiftErrorModalCtrl from '../edit-shift-status-modals/edit-error-modal.ctrl';
import { captureSentryMessage } from '../../../shared/errorHandler';
/**
 * @typedef {import("moment-timezone").Moment} Moment
 */

export const ReasonCode = {
  JOB_DESCRIPTION_OR_DETAILS_CHANGED: 0,
  START_OR_END_DATE_CHANGED: 1,
  SCHEDULE_CHANGED: 2,
  NO_LONGER_NEEDED: 3,
  SENT_HOME_UNQUALIFIED: 24,
  OTHER: 4,
};

const OVERFILL_CODE = 25;
const UNQUALIFIED_HOURS_STATUS = 1;

class CancelShiftModalController {
  /* @ngInject */
  constructor ($mdDialog, $window, Jobs, Payroll, selectedShift, selectedUser, shiftJob, shiftInProgress) {
    this.$mdDialog = $mdDialog;
    this.Jobs = Jobs;
    this.Payroll = Payroll;
    this.$window = $window;
    this.selectedShift = selectedShift;
    this.selectedUser = selectedUser[0];
    this.shiftJob = shiftJob;
    this.shiftInProgress = shiftInProgress;

    this.dropdownOptions = [
      {
        label: 'Job description or details changed',
        value: ReasonCode.JOB_DESCRIPTION_OR_DETAILS_CHANGED
      },
      {
        label: 'Start/end date changed',
        value: ReasonCode.START_OR_END_DATE_CHANGED
      },
      { label: 'Schedule changed', value: ReasonCode.SCHEDULE_CHANGED },
      { label: 'Sent Home (Overfill)', value: ReasonCode.NO_LONGER_NEEDED },
      { label: 'Sent Home (Unqualified)', value: ReasonCode.SENT_HOME_UNQUALIFIED },
      { label: 'Other', value: ReasonCode.OTHER }
    ];
  }
  
  showErrorModal = (message) => {
    const parentEl = document.body;
    return this.$mdDialog.show({
      parent: parentEl,
      fullscreen: true,
      template: editShiftErrorModal,
      controller: editShiftErrorModalCtrl,
      controllerAs: 'e',
      locals:{
        message: message,
      },
    });
  }

  onCancelShift = ({ reasonCode, otherReasonCode }) => {
    if (!this.shiftJob) {
      captureSentryMessage(
        `[CANCEL SHIFT] Missing shiftJob in modal - jobId: ${this.selectedShift?.job_id}, workerId: ${this.selectedUser?.id}`,
        'error'
      );
      this.onClose();
      this.showErrorModal('Shift information is incomplete. Please refresh the page and try again.');
      return;
    }
    
    if (this.shiftJob.jobShiftsCount === undefined || this.shiftJob.jobShiftsCount === null) {
      captureSentryMessage(
        `[CANCEL SHIFT] Missing jobShiftsCount - jobId: ${this.selectedShift?.job_id}, workerId: ${this.selectedUser?.id}`,
        'error'
      );
      this.onClose();
      this.showErrorModal('Could not load shift count. Please refresh the page and try again.');
      return;
    }

    const reason = this.dropdownOptions.find(rc => rc.value === reasonCode);
    const reasonText = otherReasonCode || reason.label;
    const {
      job_id: jobId,
      start_time,
      end_time,
      timezone,
      shift,
    } = this.selectedShift;
    const { external_id: jobExternalId } = this.selectedShift.job;
    const currentTime = moment.tz(Date.now(),timezone);
    const startTime = moment.tz(start_time, timezone);
    const endTime = moment.tz(end_time, timezone);
    const {
      id: workerId,
      external_id: workerExternalId
    } = this.selectedUser;

    if(reasonCode === ReasonCode.SENT_HOME_UNQUALIFIED){
      this._unqualifiedRemovedFromShift(workerExternalId, jobExternalId, shift, reasonCode, reasonText)
    } else if(reasonCode === ReasonCode.NO_LONGER_NEEDED &&
      currentTime.isSameOrAfter(moment(startTime).subtract(30, 'm')) &&
      currentTime.isSameOrBefore(endTime)
    ) {
      this._overFillRemovedFromShift(workerExternalId, jobExternalId, shift, reasonCode, reasonText);
    } else {
      this._removeShift(reasonCode, reasonText, jobId, startTime, endTime, timezone, workerId);
    }
  }

  onClose = () => {
    this.$mdDialog.cancel();
  }


  /**
   * calls Payroll.updateHoursStatus with payroll Hours Status code 3
   * @param {number} workerId  - worker external id
   * @param {number} jobId  - job external id
   * @param {number} shift - instead of the id, this is the shift property on selected shift.
   * @param {number} reasonCode number - ReasonCode enum value 0 through 4
   * @param {string} reasonText
   * @private
   */
  _overFillRemovedFromShift = (workerId, jobId, shift, reasonCode, reasonText) => {
    this.Payroll.updateHoursStatus(reasonCode, workerId, jobId, shift, OVERFILL_CODE, reasonText)
      .then(() => this.$window.location.reload())
      .catch((error) => error && this.showErrorModal());
  }

  /**
   * calls Payroll.updateHoursStatus with payroll Hours Status code 1
   * @param {number} workerId  - worker external id
   * @param {number} jobId  - job external id
   * @param {number} shift - instead of the id, this is the shift property on selected shift.
   * @param {number} reasonCode number - ReasonCode enum value 0 through 4 (or 24)
   * @param {string} reasonText
   * @private
   */
  _unqualifiedRemovedFromShift = (workerId, jobId, shift, reasonCode, reasonText) => {
    this.Payroll.updateHoursStatus(UNQUALIFIED_HOURS_STATUS, workerId, jobId, shift, reasonCode, reasonText)
      .then(() => this.$window.location.reload())
      .catch((error) => error && this.showErrorModal());
  }

  /**
   * Calls Jobs.removeShift API then reloads the window in order to remove the shift tile from the view
   * @param {ReasonCode} reasonCode
   * @param {string} reasonText
   * @param {number} jobId
   * @param {Moment} startTime
   * @param {Moment} endTime
   * @param {string} timezone
   * @param {number} workerId
   * @private
   */
  _removeShift = (reasonCode, reasonText, jobId, startTime, endTime, timezone, workerId) => {
    const removeShifts = {
      startDateTime: startTime.valueOf(),
      endDateTime: endTime.valueOf(),
    };
    
    if (this.shiftJob.jobShiftsCount > 1) {
      this.Jobs.removeShifts({
        jobId,
        workerId,
        removeShifts,
        reasonCode,
        reasonText,
      })
        .then(() => this.$window.location.reload())
        .catch((error) => error && this.showErrorModal());
    } else if (this.shiftJob.jobShiftsCount === 0) {
      this.onClose();
      this.showErrorModal('Number of shifts cannot be zero. Refresh the page and try again.');
    } else {
      this.Jobs.endAssignmentPreStart({ reasonCode, reasonText, jobId, workerId })
        .then(() => this.$window.location.reload())
        .catch((error) => error && this.showErrorModal());
    }
    this.onClose();
  }

}

export default CancelShiftModalController;
